//#region Imports Packages and Libs

import { TranscriptApiResponse, TranscriptStatus } from "../../types/components/transcript";
import { StringUtils } from "../../helpers/string_utils";
import { ErrorHandler } from "../../helpers/error_handler";

//#endregion

//#region Response Handler Class

/**
 * Class responsible for handling transcript API responses
 */
export class TranscriptResponseHandler {
	//#region Constants

	/** Maximum character length for error messages */
	private static readonly MAX_ERROR_LENGTH = 500;

	//#endregion

	//#region Public Methods

	/**
	 * Processes the API response from the transcript endpoint
	 * @param {any} response - The raw API response
	 * @returns {TranscriptApiResponse} - Normalized API response
	 */
	public static processResponse(response: any): TranscriptApiResponse {
		try {
			// Validate and normalize the response
			if (this.isValidResponse(response)) {
				// Normalize status to lowercase for case-insensitive comparison
				const isSuccess = StringUtils.normalizeString(response.status) === String(TranscriptStatus.Success);
				return {
					status: isSuccess ? TranscriptStatus.Success : TranscriptStatus.Fail,
					status_message: StringUtils.truncateMessage(response.status_message || "", this.MAX_ERROR_LENGTH),
				};
			} else {
				// Return error response if API response is invalid
				return this.createErrorResponseWithMessage("Invalid response received from server");
			}
		} catch (ex) {
			console.error("[TranscriptResponseHandler.processResponse] Error processing response:", ex);
			return this.createErrorResponseWithMessage("An error occurred while processing the response");
		}
	}

	/**
	 * Creates an error response for failed transcript requests
	 * @param {any} error - The error that occurred
	 * @returns {TranscriptApiResponse} - Standardized error response
	 */
	public static createErrorResponse(error: any): TranscriptApiResponse {
		return {
			status: TranscriptStatus.Fail,
			status_message: ErrorHandler.getErrorMessage(error, this.MAX_ERROR_LENGTH),
		};
	}

	/**
	 * Creates an error response with a custom message
	 * @param {string} message - Custom error message
	 * @return {TranscriptApiResponse} - Standardized error response
	 */
	public static createErrorResponseWithMessage(message: string): TranscriptApiResponse {
		return {
			status: TranscriptStatus.Fail,
			status_message: StringUtils.truncateMessage(message, this.MAX_ERROR_LENGTH),
		};
	}

	//#endregion

	//#region Private Helpers

	/**
	 * Validates if the API response is properly formatted
	 * @param {any} response - The response object to validate
	 * @returns {boolean} - True if response is valid, false otherwise
	 */
	private static isValidResponse(response: any): boolean {
		if (!response || typeof response !== "object") {
			return false;
		}

		// Check if response has required properties
		return response.hasOwnProperty("status") && response.hasOwnProperty("status_message");
	}

	//#endregion
}

//#endregion
